// AddDesign.js - With OVH Storage for Design Images
import "./AddDesign.css"
import { Stage, Layer, Image as KonvaImage, Transformer, Rect } from "react-konva";
import useImage from "use-image";
import { Fragment, useRef, useEffect, useState, useCallback } from "react";
import { UilImageDownload, UilTrashAlt, UilCheck } from "@iconscout/react-unicons";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import { ToastContainer, toast } from 'react-toastify';
import { useSelector } from "react-redux";
import { getImageUrl, toProxiedUrl } from "../../utils/imageProxyUtils";

// â”€â”€â”€ Axios instance (no interceptor) â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || "http://localhost:4000/api",
  timeout: 120000,
});

// ============================================
// STORAGE KEYS FOR PERSISTENCE
// ============================================
const DESIGN_STORAGE_KEY = "add_design_form_data";
const DESIGN_TIMESTAMP_KEY = "add_design_form_timestamp";

const saveFormToLocalStorage = (data) => {
  try {
    const serializableData = {
      formData: data.formData,
      selectedColors: data.selectedColors,
      productId: data.productId,
      tagInput: data.tagInput,
      displayedColor: data.displayedColor,
    };
    localStorage.setItem(DESIGN_STORAGE_KEY, JSON.stringify(serializableData));
    localStorage.setItem(DESIGN_TIMESTAMP_KEY, Date.now().toString());
    console.log("Design form saved to localStorage");
  } catch (error) {
    console.error("Error saving design form to localStorage:", error);
  }
};

const loadFormFromLocalStorage = () => {
  try {
    const savedData = localStorage.getItem(DESIGN_STORAGE_KEY);
    const timestamp = localStorage.getItem(DESIGN_TIMESTAMP_KEY);
    if (savedData && timestamp) {
      const parsedData = JSON.parse(savedData);
      const savedTime = parseInt(timestamp);
      const currentTime = Date.now();
      const hoursSinceSave = (currentTime - savedTime) / (1000 * 60 * 60);
      if (hoursSinceSave < 24) return parsedData;
    }
    return null;
  } catch (error) {
    console.error("Error loading design form from localStorage:", error);
    return null;
  }
};

const clearFormFromLocalStorage = () => {
  localStorage.removeItem(DESIGN_STORAGE_KEY);
  localStorage.removeItem(DESIGN_TIMESTAMP_KEY);
  console.log("Design form cleared from localStorage");
};

const getDefaultFormData = () => ({
  _id: "",
  title: "",
  theme: "",
  cost: 0,
  discount: 0,
  description: "",
  price: 0,
  tags: [],
  designType: ["DTF"],
  isPreorder: false,
  preorderAvailableAt: "",
  isPublishedB2B: true,
  isPublishedAdmin: true,
});

const DESIGN_TYPE_OPTIONS = ["Sublimation", "DTF", "Broderie"];

// ============================================
// PIXEL MANIPULATION HELPERS (for preview only)
// ============================================
const BACKGROUND_TOLERANCE = 60;
const TRANSITION_ZONE = 3;
const TRANSPARENCY_DETECTION_THRESHOLD = 0.05;

const parseColor = (color) => {
  if (!color) return [200, 200, 200];
  const rgbMatch = color.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
  if (rgbMatch) return [parseInt(rgbMatch[1]), parseInt(rgbMatch[2]), parseInt(rgbMatch[3])];
  const rgbaMatch = color.match(/rgba\((\d+),\s*(\d+),\s*(\d+)/);
  if (rgbaMatch) return [parseInt(rgbaMatch[1]), parseInt(rgbaMatch[2]), parseInt(rgbaMatch[3])];
  if (color.startsWith('#')) {
    let hex = color.replace('#', '');
    if (hex.length === 3) hex = hex.split('').map(c => c + c).join('');
    return [parseInt(hex.slice(0,2),16), parseInt(hex.slice(2,4),16), parseInt(hex.slice(4,6),16)];
  }
  try {
    const div = document.createElement('div');
    div.style.color = color; div.style.display = 'none';
    document.body.appendChild(div);
    const computed = window.getComputedStyle(div).color;
    document.body.removeChild(div);
    const m = computed.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
    if (m) return [parseInt(m[1]), parseInt(m[2]), parseInt(m[3])];
  } catch (e) {}
  return [200, 200, 200];
};

const hasExistingTransparency = (data, w, h) => {
  let transparentCount = 0;
  let total = 0;
  for (let x = 0; x < w; x++) {
    if (data[x * 4 + 3] < 10) transparentCount++;
    total++;
    const bi = ((h - 1) * w + x) * 4;
    if (data[bi + 3] < 10) transparentCount++;
    total++;
  }
  for (let y = 0; y < h; y++) {
    if (data[(y * w) * 4 + 3] < 10) transparentCount++;
    total++;
    if (data[(y * w + w - 1) * 4 + 3] < 10) transparentCount++;
    total++;
  }
  return (transparentCount / total) >= TRANSPARENCY_DETECTION_THRESHOLD;
};

const colorDistRGB = (r1,g1,b1,r2,g2,b2) => Math.sqrt((r1-r2)**2 + (g1-g2)**2 + (b1-b2)**2);

const getBackgroundAlphaMap = (data, w, h) => {
  const bgR = data[0], bgG = data[1], bgB = data[2];
  const alphaMap = new Float32Array(w * h).fill(255);
  const visited = new Uint8Array(w * h);
  const queue = [];
  const isBackground = (idx) => colorDistRGB(data[idx*4], data[idx*4+1], data[idx*4+2], bgR, bgG, bgB) <= BACKGROUND_TOLERANCE;
  for (let x = 0; x < w; x++) { [x, (h-1)*w+x].forEach(idx => { if (!visited[idx] && isBackground(idx)) { visited[idx]=1; alphaMap[idx]=0; queue.push(idx); } }); }
  for (let y = 0; y < h; y++) { [y*w, y*w+(w-1)].forEach(idx => { if (!visited[idx] && isBackground(idx)) { visited[idx]=1; alphaMap[idx]=0; queue.push(idx); } }); }
  while (queue.length > 0) {
    const idx = queue.pop();
    const x = idx % w, y = Math.floor(idx / w);
    const neighbors = [y>0? idx-w:-1, y<h-1? idx+w:-1, x>0? idx-1:-1, x<w-1? idx+1:-1];
    for (const nIdx of neighbors) {
      if (nIdx < 0 || nIdx >= w*h || visited[nIdx]) continue;
      if (isBackground(nIdx)) { visited[nIdx]=1; alphaMap[nIdx]=0; queue.push(nIdx); }
    }
  }
  const smoothAlpha = new Float32Array(alphaMap);
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const idx = y*w+x;
      if (alphaMap[idx] > 0) continue;
      for (let dy = -TRANSITION_ZONE; dy <= TRANSITION_ZONE; dy++) {
        for (let dx = -TRANSITION_ZONE; dx <= TRANSITION_ZONE; dx++) {
          const nx = x+dx, ny = y+dy;
          if (nx<0||nx>=w||ny<0||ny>=h) continue;
          const nIdx = ny*w+nx;
          if (alphaMap[nIdx] === 0) continue;
          const dist = Math.sqrt(dx*dx+dy*dy);
          if (dist <= TRANSITION_ZONE) {
            const fade = (dist/TRANSITION_ZONE)**2 * 255;
            if (fade < smoothAlpha[nIdx]) smoothAlpha[nIdx] = fade;
          }
        }
      }
    }
  }
  return smoothAlpha;
};

const buildTintedCanvas = (img, color) => {
  const w = img.width;
  const h = img.height;
  const canvas = document.createElement("canvas");
  canvas.width = w; canvas.height = h;
  const ctx = canvas.getContext("2d");
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  ctx.clearRect(0, 0, w, h);
  ctx.drawImage(img, 0, 0, w, h);
  const imageData = ctx.getImageData(0, 0, w, h);
  const data = imageData.data;
  const [tR, tG, tB] = parseColor(color || "#cccccc");
  const colorLuma = (0.299 * tR + 0.587 * tG + 0.114 * tB) / 255;
  const alreadyTransparent = hasExistingTransparency(data, w, h);
  const alphaMap = alreadyTransparent ? null : getBackgroundAlphaMap(data, w, h);
  for (let i = 0; i < data.length; i += 4) {
    const pixelIdx = i / 4;
    const or = data[i], og = data[i+1], ob = data[i+2], oa = data[i+3];
    const finalAlpha = alreadyTransparent ? oa : (alphaMap[pixelIdx] < 1 ? 0 : Math.round((oa/255) * alphaMap[pixelIdx]));
    if (finalAlpha < 1) {
      data[i]=0; data[i+1]=0; data[i+2]=0; data[i+3]=0;
      continue;
    }
    const brightness = (or + og + ob) / 3;
    const factor = brightness / 255;
    let oR = Math.min(255, Math.round(tR * factor));
    let oG = Math.min(255, Math.round(tG * factor));
    let oB = Math.min(255, Math.round(tB * factor));
    if (colorLuma < 0.25) {
      const darkness = 1 - colorLuma / 0.25;
      const overlay = Math.round((brightness / 255) * 60 * darkness);
      oR = Math.min(255, oR + overlay);
      oG = Math.min(255, oG + overlay);
      oB = Math.min(255, oB + overlay);
    }
    data[i] = oR; data[i+1] = oG; data[i+2] = oB;
    data[i+3] = finalAlpha;
  }
  ctx.putImageData(imageData, 0, 0);
  return canvas;
};

const useTintedCanvas = (imageUrl, color) => {
  const [canvas, setCanvas] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const requestId = useRef(0);
  useEffect(() => {
    if (!imageUrl) { setCanvas(null); return; }
    const proxiedImageUrl = toProxiedUrl(imageUrl);
    const id = ++requestId.current;
    setIsLoading(true);
    const img = new window.Image();
    img.crossOrigin = 'Anonymous';
    img.onload = () => {
      if (id !== requestId.current) return;
      setCanvas(buildTintedCanvas(img, color));
      setIsLoading(false);
    };
    img.onerror = () => { if (id === requestId.current) setIsLoading(false); };
    img.src = proxiedImageUrl;
  }, [imageUrl, color]);
  return { canvas, isLoading };
};

// ============================================
// DESIGN ITEM COMPONENT - PREVIEW ONLY
// ============================================
const DesignItem = ({ design, isSelected, onSelect, onUpdate, onDelete }) => {
  const imageRef = useRef();
  const trRef = useRef();
  const [deleteIcon] = useImage("/corbeille.png");
  useEffect(() => {
    if (isSelected && trRef.current && imageRef.current) {
      trRef.current.nodes([imageRef.current]);
      trRef.current.getLayer()?.batchDraw();
    }
  }, [isSelected]);
  const handleDragMove = (e) => { onUpdate({ ...design, x: e.target.x(), y: e.target.y() }); };
  const handleTransform = (e) => {
    const node = imageRef.current;
    onUpdate({ ...design, x: node.x(), y: node.y(), scaleX: node.scaleX(), scaleY: node.scaleY(), rotation: node.rotation() });
  };
  const handleClick = (e) => { e.cancelBubble = true; onSelect(design.id); };
  return (
    <Fragment>
      <KonvaImage image={design.image} x={design.x} y={design.y} width={design.originalWidth} height={design.originalHeight} scaleX={design.scaleX || 1} scaleY={design.scaleY || 1} rotation={design.rotation || 0} onClick={handleClick} onTap={handleClick} onDragMove={handleDragMove} onTransform={handleTransform} ref={imageRef} draggable imageSmoothingEnabled={true} imageSmoothingQuality="high" />
      {isSelected && (
        <Fragment>
          <Transformer ref={trRef} anchorSize={10} borderEnabled={true} borderStroke="#0B2A5C" borderStrokeWidth={2} rotateEnabled={true} rotationSnaps={[0, 90, 180, 270]} keepRatio={true} enabledAnchors={['top-left','top-right','bottom-left','bottom-right']} anchorStyleFunc={(anchor) => { anchor.cornerRadius(5); anchor.fill('#0B2A5C'); anchor.stroke('#ffffff'); anchor.strokeWidth(2); }} />
          {deleteIcon && <KonvaImage image={deleteIcon} onClick={(e) => { e.cancelBubble = true; onDelete(design.id); }} width={24} height={24} onMouseOver={() => (document.body.style.cursor = "pointer")} onMouseOut={() => (document.body.style.cursor = "default")} x={design.x + (design.originalWidth * (design.scaleX || 1)) / 2 - 12} y={design.y + design.originalHeight * (design.scaleY || 1) + 10} />}
        </Fragment>
      )}
    </Fragment>
  );
};

// ============================================
// MAIN COMPONENT
// ============================================
export const AddDesign = () => {
  const navigate = useNavigate();
  const { product_id: productIdFromUrl } = useParams();
  const { currentUser } = useSelector((state) => state.auth);
  const [products, setProducts] = useState([]);
  const [productId, setProductId] = useState("");
  const [loading, setLoading] = useState(true);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [loadingProduct, setLoadingProduct] = useState(false);
  const [formData, setFormData] = useState(getDefaultFormData());
  const [tagInput, setTagInput] = useState("");
  const [displayedColor, setDisplayedColor] = useState("");
  const [selectedColors, setSelectedColors] = useState([]);
  const [availableColors, setAvailableColors] = useState([]);
  const [frontDesignFiles, setFrontDesignFiles] = useState([]);
  const [backDesignFiles, setBackDesignFiles] = useState([]);
  const [galleryImages, setGalleryImages] = useState([]);
  const [frontDesigns, setFrontDesigns] = useState([]);
  const [backDesigns, setBackDesigns] = useState([]);
  const [selectedDesignId, setSelectedDesignId] = useState(null);
  const [selectedSide, setSelectedSide] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [themes, setThemes] = useState([]);
  const [showRestorePrompt, setShowRestorePrompt] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const frontStageRef = useRef(null);
  const backStageRef = useRef(null);
  const galleryImagesRef = useRef([]);
  const isSelectedProductFinished = selectedProduct?.isFinishedProduct === true;

  useEffect(() => {
    galleryImagesRef.current = galleryImages;
  }, [galleryImages]);

  useEffect(() => {
    return () => {
      galleryImagesRef.current.forEach((image) => URL.revokeObjectURL(image.previewUrl));
    };
  }, []);

  // â”€â”€ Sync token to axios after Redux rehydration â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  useEffect(() => {
    const rawToken = currentUser?.token || null;
    const refreshToken = currentUser?.refreshToken || null;
    if (rawToken) {
      const token = rawToken.startsWith("Bearer ") ? rawToken : `Bearer ${rawToken}`;
      api.defaults.headers.common["Authorization"] = token;
      console.log("dmin token synced to axios (AddDesign)");
    } else {
      delete api.defaults.headers.common["Authorization"];
      console.warn("No token in currentUser — admin may not be logged in");
    }
    if (refreshToken) {
      api.defaults.headers.common["x-refresh-token"] = refreshToken;
    } else {
      delete api.defaults.headers.common["x-refresh-token"];
    }
  }, [currentUser]);

  // â”€â”€ Persistence effects (unchanged) â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  useEffect(() => {
    if (!isInitialLoad && hasUnsavedChanges && productId) {
      saveFormToLocalStorage({ formData, selectedColors, productId, tagInput, displayedColor });
    }
  }, [formData, selectedColors, productId, tagInput, displayedColor, isInitialLoad, hasUnsavedChanges]);

  useEffect(() => {
    if (!isInitialLoad && productId) setHasUnsavedChanges(true);
  }, [formData, selectedColors, productId, tagInput, displayedColor]);

  useEffect(() => {
    const checkForSavedForm = () => {
      const savedFormState = loadFormFromLocalStorage();
      if (savedFormState && savedFormState.formData) setShowRestorePrompt(true);
      setIsInitialLoad(false);
    };
    checkForSavedForm();
  }, []);

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges && !isInitialLoad && productId) {
        e.preventDefault();
        e.returnValue = "Vous avez des modifications non sauvegardées. Voulez-vous vraiment quitter ?";
        return e.returnValue;
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges, isInitialLoad, productId]);

  const restoreSavedForm = async () => {
    const savedFormState = loadFormFromLocalStorage();
    if (savedFormState) {
      if (savedFormState.formData) setFormData(savedFormState.formData);
      if (savedFormState.selectedColors) setSelectedColors(savedFormState.selectedColors);
      if (savedFormState.tagInput) setTagInput(savedFormState.tagInput);
      if (savedFormState.displayedColor) setDisplayedColor(savedFormState.displayedColor);
      if (savedFormState.productId) {
        setProductId(savedFormState.productId);
        setLoadingProduct(true);
        try {
          const res = await api.get(`/products/${savedFormState.productId}`);
          if (res.data.product) {
            setSelectedProduct(res.data.product);
            if (!res.data.product.isFinishedProduct) {
              const colors = Object.keys(res.data.product.colorsAndSizes || {});
              setAvailableColors(colors);
              if (savedFormState.selectedColors && savedFormState.selectedColors.length > 0) {
                setSelectedColors(savedFormState.selectedColors);
                setDisplayedColor(savedFormState.displayedColor || savedFormState.selectedColors[0]);
              } else if (colors.length > 0) {
                setSelectedColors([colors[0]]);
                setDisplayedColor(colors[0]);
              }
            }
            setFormData(prev => ({ ...prev, description: res.data.product.description || "", tags: res.data.product.tags || [] }));
          }
        } catch (err) { console.error("Error loading saved product:", err); }
        finally { setLoadingProduct(false); }
      }
      toast.success("Formulaire restauré avec succès", { autoClose: 3000 });
    }
    setShowRestorePrompt(false);
    setHasUnsavedChanges(false);
  };

  const clearSavedForm = () => {
    clearFormFromLocalStorage();
    setShowRestorePrompt(false);
    setHasUnsavedChanges(false);
    toast.info("Formulaire sauvegardé effacé", { autoClose: 2000 });
  };

  const clearGalleryImages = useCallback(() => {
    setGalleryImages((prev) => {
      prev.forEach((image) => URL.revokeObjectURL(image.previewUrl));
      return [];
    });
  }, []);

  const resetForm = () => {
    setFormData(getDefaultFormData());
    setSelectedColors([]);
    setProductId("");
    setSelectedProduct(null);
    setTagInput("");
    setDisplayedColor("");
    setFrontDesignFiles([]);
    setBackDesignFiles([]);
    clearGalleryImages();
    setFrontDesigns([]);
    setBackDesigns([]);
    setSelectedDesignId(null);
    setSelectedSide(null);
    setAvailableColors([]);
    clearFormFromLocalStorage();
    setHasUnsavedChanges(false);
    toast.success("Formulaire réinitialisé", { autoClose: 2000 });
  };

  const getProductImageUrl = (product, side = 'front') => {
    if (!product?.imgs?.length) return null;
    const image = product.imgs.find(img => img.type === side);
    if (image) return getImageUrl(image);
    const fallback = product.imgs[side === 'front' ? 0 : 1] || product.imgs[0];
    return getImageUrl(fallback);
  };
  const productFrontImageUrl = getProductImageUrl(selectedProduct, 'front');
  const productBackImageUrl = getProductImageUrl(selectedProduct, 'back');
  const { canvas: tintedFront, isLoading: loadingFront } = useTintedCanvas(productFrontImageUrl, displayedColor);
  const { canvas: tintedBack, isLoading: loadingBack } = useTintedCanvas(productBackImageUrl, displayedColor);

  const handleBackgroundClick = (side) => { setSelectedDesignId(null); setSelectedSide(null); };

  const fetchProductsForDropdown = useCallback(async () => {
    try {
      setLoading(true);
      const timestamp = Date.now();
      const res = await api.get(`/products/designs?_t=${timestamp}`);
      setProducts(res.data.products || []);
      console.log("Loaded products for designs:", res.data.products?.length);
    } catch (err) {
      console.error("Error fetching products:", err);
      toast.error("Erreur lors du chargement des produits");
    } finally { setLoading(false); }
  }, []);

  const fetchThemes = useCallback(async () => {
    try {
      const res = await api.get("/themes");
      setThemes(res.data.themes || []);
    } catch (err) { console.error("Erreur chargement themes:", err); }
  }, []);

  useEffect(() => {
    const loadData = async () => { await fetchProductsForDropdown(); await fetchThemes(); };
    loadData();
    const handleProductsChanged = () => { fetchProductsForDropdown(); };
    window.addEventListener('productsChanged', handleProductsChanged);
    return () => window.removeEventListener('productsChanged', handleProductsChanged);
  }, [fetchProductsForDropdown, fetchThemes]);

  const chooseProduct = async (id) => {
    if (!id) { setProductId(""); setSelectedProduct(null); setDisplayedColor(""); setSelectedColors([]); setAvailableColors([]); setFrontDesignFiles([]); setBackDesignFiles([]); clearGalleryImages(); setFrontDesigns([]); setBackDesigns([]); return; }
    setProductId(id);
    setLoadingProduct(true);
    try {
      const res = await api.get(`/products/${id}`);
      if (res.data.product) {
        setSelectedProduct(res.data.product);
        if (!res.data.product.isFinishedProduct) {
          const colors = Object.keys(res.data.product.colorsAndSizes || {});
          setAvailableColors(colors);
          if (colors.length > 0 && selectedColors.length === 0) {
            setSelectedColors([colors[0]]);
            setDisplayedColor(colors[0]);
          }
        } else {
          setDisplayedColor("");
          setAvailableColors([]);
          setSelectedColors([]);
        }
        setFormData(prev => ({ ...prev, description: res.data.product.description || "", tags: res.data.product.tags || [] }));
        if (res.data.product.isFinishedProduct) {
          setFormData(prev => ({ ...prev, price: (res.data.product.purchase || 0) * 1.5, cost: res.data.product.purchase || 0 }));
        }
        toast.success(`Produit chargé: ${res.data.product._id}`, { autoClose: 2000 });
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Erreur lors du chargement du produit");
      setSelectedProduct(null);
    } finally { setLoadingProduct(false); }
  };

  // Auto-select the product passed in the URL (e.g. from "Nouveau modèle" on a
  // specific product row) — this was previously ignored entirely, so the form
  // never loaded the product you actually clicked from.
  useEffect(() => {
    if (productIdFromUrl) {
      chooseProduct(productIdFromUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productIdFromUrl]);

  const handleColorToggle = (color) => {
    if (selectedColors.includes(color)) {
      setSelectedColors(prev => prev.filter(c => c !== color));
      if (displayedColor === color) setDisplayedColor(selectedColors.filter(c => c !== color)[0] || "");
    } else {
      setSelectedColors(prev => [...prev, color]);
      if (!displayedColor) setDisplayedColor(color);
    }
  };

  const handleSelectAllColors = () => {
    setSelectedColors([...availableColors]);
    if (availableColors.length > 0 && !displayedColor) setDisplayedColor(availableColors[0]);
    toast.success(`Toutes les couleurs sélectionnées (${availableColors.length})`);
  };
  const handleDeselectAllColors = () => { setSelectedColors([]); setDisplayedColor(""); toast.info(`Aucune couleur sélectionnée`); };

  const handleDeleteDesign = (side, designId) => {
    if (side === "front") {
      const index = frontDesigns.findIndex(d => d.id === designId);
      if (index !== -1) { setFrontDesignFiles(prev => prev.filter((_, i) => i !== index)); setFrontDesigns(prev => prev.filter(design => design.id !== designId)); }
    } else {
      const index = backDesigns.findIndex(d => d.id === designId);
      if (index !== -1) { setBackDesignFiles(prev => prev.filter((_, i) => i !== index)); setBackDesigns(prev => prev.filter(design => design.id !== designId)); }
    }
    if (selectedDesignId === designId) { setSelectedDesignId(null); setSelectedSide(null); }
    toast.success(`Design supprimé`, { autoClose: 1500 });
  };

  const handleDesignUpload = (side, e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    files.forEach((file, index) => {
      if (side === "front") setFrontDesignFiles(prev => [...prev, file]);
      else setBackDesignFiles(prev => [...prev, file]);
      const reader = new FileReader();
      reader.onload = (event) => {
        const img = new window.Image();
        img.src = event.target.result;
        img.onload = () => {
          const existingCount = (side === "front" ? frontDesigns.length : backDesigns.length) + index;
          const originalWidth = img.width, originalHeight = img.height;
          const initialScale = Math.min(0.8, 400 / Math.max(originalWidth, originalHeight));
          const initialWidth = originalWidth * initialScale, initialHeight = originalHeight * initialScale;
          const initialX = (500 - initialWidth) / 2 + (existingCount * 20);
          const initialY = (500 - initialHeight) / 2 + (existingCount * 20);
          const newDesign = { id: `${Date.now()}_${index}_${Math.random()}`, image: img, originalWidth, originalHeight, x: initialX, y: initialY, width: originalWidth, height: originalHeight, scaleX: initialScale, scaleY: initialScale, rotation: 0, name: file.name };
          if (side === "front") setFrontDesigns(prev => [...prev, newDesign]);
          else setBackDesigns(prev => [...prev, newDesign]);
        };
      };
      reader.readAsDataURL(file);
    });
    toast.success(`${files.length} design(s) ajouté(s) sur le ${side === 'front' ? 'devant' : 'dos'}`, { autoClose: 2000 });
    e.target.value = null;
    setHasUnsavedChanges(true);
  };

  const handleGalleryImageUpload = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    const newImages = files.map((file, index) => ({
      id: `${Date.now()}_${index}_${Math.random()}`,
      file,
      previewUrl: URL.createObjectURL(file),
      name: file.name,
      size: file.size,
    }));

    setGalleryImages((prev) => [...prev, ...newImages]);
    setHasUnsavedChanges(true);
    toast.success(`${files.length} image(s) ajoutée(s) au modèle`, { autoClose: 2000 });
    e.target.value = null;
  };

  const handleDeleteGalleryImage = (imageId) => {
    setGalleryImages((prev) => {
      const imageToRemove = prev.find((image) => image.id === imageId);
      if (imageToRemove) URL.revokeObjectURL(imageToRemove.previewUrl);
      return prev.filter((image) => image.id !== imageId);
    });
    setHasUnsavedChanges(true);
    toast.success("Image supprimée", { autoClose: 1500 });
  };

  const handleUpdateDesign = (side, updatedDesign) => {
    if (side === "front") setFrontDesigns(prev => prev.map(design => design.id === updatedDesign.id ? { ...updatedDesign, originalWidth: design.originalWidth, originalHeight: design.originalHeight, image: design.image } : design));
    else setBackDesigns(prev => prev.map(design => design.id === updatedDesign.id ? { ...updatedDesign, originalWidth: design.originalWidth, originalHeight: design.originalHeight, image: design.image } : design));
  };

  const handleSelectDesign = (side, designId) => { setSelectedDesignId(designId); setSelectedSide(side); };
  const downloadURI = (uri, name) => { const link = document.createElement("a"); link.download = name; link.href = uri; document.body.appendChild(link); link.click(); document.body.removeChild(link); };
  const handleExport = (side) => {
    const stageRef = side === "front" ? frontStageRef : backStageRef;
    if (!stageRef.current) return;
    const transformer = stageRef.current.findOne('Transformer');
    if (transformer) transformer.visible(false);
    const dataURL = stageRef.current.toDataURL({ mimeType: "image/png", pixelRatio: 3840 / 500, quality: 1 });
    if (transformer) transformer.visible(true);
    downloadURI(dataURL, `${formData.title || 'design'}-${side}-4k.png`);
    toast.success(`Design exporté en 4K`, { autoClose: 2000 });
  };
  const getTotalCost = () => (parseFloat(selectedProduct?.purchase) || 0) + (parseFloat(formData.cost) || 0);
  const getPriceAfterDiscount = () => { const price = parseFloat(formData.price) || 0; const discount = parseFloat(formData.discount) || 0; return discount > 0 ? price * (1 - discount / 100) : price; };
  const formatFileSize = (size) => size < 1024 * 1024 ? `${Math.max(1, Math.round(size / 1024))} KB` : `${(size / (1024 * 1024)).toFixed(1)} MB`;
  const formatPreorderDate = (value) => {
    if (!value) return "";
    const [year, month, day] = String(value).split("-");
    if (!year || !month || !day) return "";
    return new Date(Number(year), Number(month) - 1, Number(day)).toLocaleDateString("fr-FR", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  };

  const handleSubmit = async () => {
    if (!formData.title) { toast.error('Veuillez entrer un titre'); return; }
    if (!selectedProduct?._id) { toast.error('Veuillez sélectionner un produit'); return; }
    if (!isSelectedProductFinished && selectedColors.length === 0) { toast.error('Veuillez sélectionner au moins une couleur pour ce design'); return; }
    if (!isSelectedProductFinished && frontDesignFiles.length === 0 && backDesignFiles.length === 0 && galleryImages.length === 0) { toast.error('Veuillez ajouter au moins une image ou un design'); return; }
    if (formData.isPreorder && !formData.preorderAvailableAt) { toast.error('Veuillez choisir la date de disponibilite'); return; }
    setIsSubmitting(true);
    try {
      const formDataToSend = new FormData();
      formDataToSend.append('title', formData.title);
      formDataToSend.append('linkedProduct', selectedProduct._id);
      formDataToSend.append('price', formData.price.toString());
      formDataToSend.append('cost', formData.cost.toString());
      formDataToSend.append('description', (selectedProduct?.description || "") + " \n " + (formData.description || ""));
      formDataToSend.append('theme', formData.theme);
      formDataToSend.append('discount', formData.discount.toString());
      formDataToSend.append('tags', JSON.stringify(formData.tags));
      formDataToSend.append('designType', JSON.stringify(formData.designType?.length ? formData.designType : ["DTF"]));
      formDataToSend.append('isPublishedB2B', formData.isPublishedB2B.toString());
      formDataToSend.append('isPublishedAdmin', formData.isPublishedAdmin.toString());
      formDataToSend.append('isFinishedProduct', isSelectedProductFinished.toString());
      formDataToSend.append('availableColors', JSON.stringify(selectedColors));
      formDataToSend.append('isPreorder', formData.isPreorder.toString());
      formDataToSend.append('preorderAvailableAt', formData.isPreorder ? formData.preorderAvailableAt : '');
      const designMetadata = { front: frontDesigns.map(d => ({ name: d.name, x: d.x, y: d.y, originalWidth: d.originalWidth, originalHeight: d.originalHeight, scaleX: d.scaleX, scaleY: d.scaleY, rotation: d.rotation })), back: backDesigns.map(d => ({ name: d.name, x: d.x, y: d.y, originalWidth: d.originalWidth, originalHeight: d.originalHeight, scaleX: d.scaleX, scaleY: d.scaleY, rotation: d.rotation })), gallery: galleryImages.map((image, index) => ({ name: image.name, size: image.size, type: `gallery-${index + 1}` })) };
      formDataToSend.append('designMetadata', JSON.stringify(designMetadata));
      const imageTypes = [];
      frontDesignFiles.forEach(file => { formDataToSend.append('images', file); imageTypes.push('front'); });
      backDesignFiles.forEach(file => { formDataToSend.append('images', file); imageTypes.push('back'); });
      galleryImages.forEach((image, index) => { formDataToSend.append('images', image.file); imageTypes.push(`gallery-${index + 1}`); });
      formDataToSend.append('imageTypes', JSON.stringify(imageTypes));
      const uploadedImagesCount = frontDesignFiles.length + backDesignFiles.length + galleryImages.length;
      console.log(`Submitting ${frontDesignFiles.length} front + ${backDesignFiles.length} back + ${galleryImages.length} gallery images to OVH`);
      await api.post("/designs", formDataToSend, { headers: { 'Content-Type': 'multipart/form-data' } });
      clearFormFromLocalStorage();
      setHasUnsavedChanges(false);
      toast.success(isSelectedProductFinished ? 'Produit fini ajouté avec succès!' : `${selectedColors.length} couleur(s) sélectionnée(s) - ${uploadedImagesCount} image(s) sauvegardée(s) sur OVH!`);
      setTimeout(() => navigate("/designs"), 2000);
    } catch (err) {
      console.error("Submit error:", err);
      toast.error(err.response?.data?.error || 'Erreur lors de la sauvegarde');
    } finally { setIsSubmitting(false); }
  };

  const handleAddTag = () => {
    const trimmed = tagInput.trim();
    if (trimmed && !formData.tags?.includes(trimmed)) {
      setFormData({ ...formData, tags: [...formData.tags, trimmed] });
      setTagInput("");
    }
  };

  const colors = availableColors;
  if (loading) return (<div className="add-product-container"><div className="add-product-grid"><div className="add-product-grid-item"><h1>Ajouter un modèle</h1><div className="loading-spinner-large"><div className="spinner-large" /><p>Chargement...</p></div></div></div></div>);

  return (
    <div className="add-product-container">
      {showRestorePrompt && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(4px)' }}>
          <div style={{ background: 'var(--surface)', borderRadius: '24px', padding: '2rem', maxWidth: '500px', width: '90%', textAlign: 'center', boxShadow: '0 20px 60px rgba(0,0,0,0.3)', border: '1px solid var(--border)' }}>
            <h3 style={{ marginBottom: '1rem', color: 'var(--accent)' }}>📝 Formulaire non terminé</h3>
            <p style={{ marginBottom: '0.5rem', color: 'var(--text-secondary)' }}>Un formulaire de modèle non terminé a été trouvé.</p>
            <p style={{ marginBottom: '1.5rem', color: 'var(--text-muted)', fontSize: '0.85rem' }}>⚠️ Les designs devront être re-téléchargés après restauration.</p>
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
              <button onClick={restoreSavedForm} style={{ background: 'var(--accent)', color: 'white', padding: '0.75rem 1.5rem', border: 'none', borderRadius: '12px', cursor: 'pointer', fontWeight: 600 }}>Restaurer</button>
              <button onClick={clearSavedForm} style={{ background: 'var(--danger)', color: 'white', padding: '0.75rem 1.5rem', border: 'none', borderRadius: '12px', cursor: 'pointer', fontWeight: 600 }}>Ignorer</button>
            </div>
          </div>
        </div>
      )}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '1rem', gap: '0.5rem' }}>
        {hasUnsavedChanges && <span style={{ fontSize: '0.75rem', color: 'var(--accent)', padding: '0.5rem 1rem', background: 'var(--accent-soft)', borderRadius: '8px' }}>💾 Modifications non sauvegardées</span>}
        <button onClick={resetForm} style={{ background: 'var(--danger)', color: 'white', padding: '0.5rem 1rem', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '0.8rem' }}>🗑️ Réinitialiser le formulaire</button>
      </div>
      <div className="add-product-grid">
        <div className="add-product-grid-item">
                    {!currentUser && <div className="auth-alert">⚠️ Vous devez être connecté en tant qu'administrateur</div>}
          <div className="add-product-categories">
            <div><h4>Choix produit</h4><select className="add-product-input" value={productId || ""} onChange={(e) => chooseProduct(e.target.value)} disabled={loadingProduct}><option value="">-- Sélectionner un produit --</option>{products?.map((p) => (<option key={p._id} value={p._id}>{p.displayName || `${p._id} (${p.fournisseur || 'No supplier'})`} {p.isFinishedProduct ? '🏷️' : '🎨'}</option>))}</select>{loadingProduct && <div className="field-note">Chargement du produit...</div>}</div>
            <div><h4>Catégorie</h4><div className="add-product-details"><input type="text" readOnly value={selectedProduct?.category || ""} className="add-product-input-text" /></div></div>
            <div><h4>Sous-catégorie</h4><div className="add-product-details"><input type="text" readOnly value={selectedProduct?.subcategory || ""} className="add-product-input-text" /></div></div>
          </div>
          {isSelectedProductFinished && (
            <div className="quantity-container" style={{ background: 'var(--success-soft)', marginBottom: '1rem' }}>
              <h4>🏷️ Produit Fini</h4>
              <div className="field-note">Ce produit est un produit fini. Il n'a pas besoin de design personnalisé.</div>
              <div className="field-note">Le prix de vente suggéré est basé sur le prix d'achat avec une marge de 50%.</div>
              <div className="field-note">Vous pouvez modifier le prix ci-dessous.</div>
            </div>
          )}
          {!isSelectedProductFinished && colors.length > 0 && (
            <div className="quantity-container" style={{ marginBottom: '1rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '10px' }}>
                <h4 style={{ margin: 0 }}>🎨 Sélection des couleurs pour ce design</h4>
                <div style={{ display: 'flex', gap: '8px' }}><button type="button" onClick={handleSelectAllColors} style={{ padding: '4px 12px', fontSize: '11px' }}>Tout sélectionner</button><button type="button" onClick={handleDeselectAllColors} style={{ padding: '4px 12px', fontSize: '11px', background: '#666' }}>Tout désélectionner</button></div>
              </div>
              <div className="color-selection-grid" style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', marginBottom: '12px' }}>
                {colors.map((color) => (
                  <div key={color} onClick={() => handleColorToggle(color)} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px', cursor: 'pointer', padding: '8px', borderRadius: '12px', background: selectedColors.includes(color) ? 'var(--accent-soft)' : 'transparent', border: selectedColors.includes(color) ? '1.5px solid var(--accent)' : '1.5px solid transparent', transition: 'all 0.2s ease' }}>
                    <div style={{ width: '48px', height: '48px', borderRadius: '50%', backgroundColor: color, border: '2px solid white', boxShadow: '0 2px 8px rgba(0,0,0,0.15)', position: 'relative' }}>{selectedColors.includes(color) && <div style={{ position: 'absolute', bottom: '-4px', right: '-4px', width: '20px', height: '20px', background: 'var(--accent)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '12px', fontWeight: 'bold' }}><UilCheck size={14} color="white" /></div>}</div>
                    <span style={{ fontSize: '11px', fontWeight: 500, color: 'var(--text-secondary)' }}>{color}</span>
                  </div>
                ))}
              </div>
              <div className="field-note">{selectedColors.length === 0 ? <span style={{ color: 'var(--danger)' }}>⚠️ Sélectionnez au moins une couleur pour ce design</span> : <span style={{ color: 'var(--success)' }}>✅ {selectedColors.length} couleur(s) sélectionnée(s) pour ce modèle</span>}</div>
              <div className="field-note">💡 Ce design sera disponible uniquement pour les couleurs sélectionnées ci-dessus</div>
            </div>
          )}
          {!isSelectedProductFinished && colors.length === 0 && selectedProduct && (<div className="quantity-container" style={{ marginBottom: '1rem', background: '#fff8eb', border: '1px solid #ffd166' }}><div className="field-note" style={{ color: '#9a6200', textAlign: 'center' }}>⚠️ Ce produit n'a pas de couleurs définies. Veuillez d'abord ajouter des couleurs au produit.</div></div>)}
          <div className="form-group"><h4>Nom du modèle / produit</h4><div className="add-product-input"><input onChange={(e) => setFormData(p => ({ ...p, title: e.target.value }))} className="add-product-input-text" placeholder="Ex: T-shirt club ossec" value={formData.title} /></div></div>
          <div className="form-group"><h4>Description</h4><div className="add-product-input"><textarea onChange={(e) => setFormData(p => ({ ...p, description: e.target.value }))} className="add-product-input-paragraph" placeholder="Description..." value={formData.description} /></div></div>
          <div className="add-product-price">
            <div><h4>Prix d'achat produit</h4><div className="add-product-input"><input readOnly type="text" className="add-product-input-text" value={selectedProduct?.purchase || "0"} /></div><div className="field-note">💰 Prix d'achat du produit vierge (fournisseur)</div></div>
            <div><h4>Coût de production</h4><div className="add-product-input"><input type="number" className="add-product-input-text" placeholder="Ex: 5.00" onChange={(e) => setFormData(p => ({ ...p, cost: Number(e.target.value) }))} value={formData.cost} min="0" step="0.01" /></div><div className="field-note">🎨 Coût de sérigraphie / impression du design</div></div>
            <div><h4>Coût total (revient)</h4><div className="add-product-input" style={{ background: "var(--accent-soft)" }}><input readOnly type="text" className="add-product-input-text" value={getTotalCost().toFixed(2)} style={{ fontWeight: "600", color: "var(--accent)" }} /></div><div className="field-note">📊 = Prix d'achat + Coût de production</div></div>
            <div><h4>Prix de vente</h4><div className="add-product-input"><input onChange={(e) => setFormData(p => ({ ...p, price: Number(e.target.value) }))} type="number" className="add-product-input-text" placeholder="Ex: 29.99" value={formData.price} min="0" step="0.01" /></div><div className="field-note">🏷️ Prix final pour le client</div></div>
          </div>
          <div className="form-group"><h4>Promotion (%)</h4><div className="add-product-input"><input onChange={(e) => setFormData(p => ({ ...p, discount: Number(e.target.value) }))} type="number" className="add-product-input-text" placeholder="0" value={formData.discount} min="0" max="100" /></div>{formData.discount > 0 && <div className="field-note" style={{ color: 'var(--success)' }}>🎉 Prix après promotion: €{getPriceAfterDiscount().toFixed(2)}</div>}</div>
          <div className="form-group"><h4>Thème</h4><select className="add-product-input" value={formData.theme} onChange={(e) => setFormData({ ...formData, theme: e.target.value })}><option value="">Sélectionner un thème</option>{themes?.map((theme) => (<option key={theme._id} value={theme.title}>{theme.title}</option>))}</select></div>
          <div className="form-group">
            <h4>Type d'impression</h4>
            <div className="add-product-tags">
              {DESIGN_TYPE_OPTIONS.map((option) => (
                <label key={option} style={{ display: "inline-flex", alignItems: "center", gap: "0.4rem", marginRight: "1rem", cursor: "pointer" }}>
                  <input
                    type="checkbox"
                    checked={(formData.designType || []).includes(option)}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setFormData((prev) => {
                        const current = prev.designType || [];
                        return {
                          ...prev,
                          designType: checked
                            ? [...current, option]
                            : current.filter((t) => t !== option),
                        };
                      });
                    }}
                  />
                  {option}
                </label>
              ))}
            </div>
            <div className="field-note">
              Aucune sélection = DTF par défaut. Vous pouvez en choisir un ou plusieurs.
            </div>
          </div>
          <div className="form-group">
            <h4>Publication</h4>
            <div className="add-product-tags" style={{ gap: "0.5rem" }}>
              <button
                type="button"
                onClick={() => setFormData((p) => ({ ...p, isPublishedB2B: !p.isPublishedB2B }))}
                style={{ background: formData.isPublishedB2B ? "var(--success, #22c55e)" : undefined }}
              >
                {formData.isPublishedB2B ? "✔ Publié dans B2B" : "Publier dans B2B"}
              </button>
              <button
                type="button"
                onClick={() => setFormData((p) => ({ ...p, isPublishedAdmin: !p.isPublishedAdmin }))}
                style={{ background: formData.isPublishedAdmin ? "var(--success, #22c55e)" : undefined }}
              >
                {formData.isPublishedAdmin ? "✔ Publié dans Admin" : "Publier dans Admin"}
              </button>
            </div>
            <div className="field-note">
              Publié dans B2B = visible dans la boutique client. Publié dans Admin = sélectionnable dans "Nv Commande" par le staff.
            </div>
          </div>
          <div className="form-group preorder-fields">
            <h4>Statut B2B</h4>
            <div className="preorder-grid">
              <div>
                <select
                  className="add-product-input"
                  value={formData.isPreorder ? "preorder" : "standard"}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    isPreorder: e.target.value === "preorder",
                    preorderAvailableAt: e.target.value === "preorder" ? prev.preorderAvailableAt : "",
                  }))}
                >
                  <option value="standard">Catalogue standard</option>
                  <option value="preorder">Precommande</option>
                </select>
              </div>
              {formData.isPreorder && (
                <div>
                  <div className="add-product-input">
                    <input
                      type="date"
                      className="add-product-input-text"
                      value={formData.preorderAvailableAt}
                      min={new Date().toISOString().slice(0, 10)}
                      onChange={(e) => setFormData(prev => ({ ...prev, preorderAvailableAt: e.target.value }))}
                    />
                  </div>
                </div>
              )}
            </div>
            <div className="field-note">
              {formData.isPreorder
                ? `Visible dans la section precommandes${formData.preorderAvailableAt ? ` - disponible le ${formatPreorderDate(formData.preorderAvailableAt)}` : ""}`
                : "Visible dans la boutique normale"}
            </div>
            {selectedProduct?.isPreorder && (
              <div className="field-note">Le produit lie est deja en precommande. Ce modele apparaitra aussi en precommande.</div>
            )}
          </div>
          <div className="quantity-container">
            <h4>Quantité disponible</h4>
            <div className="add-product-colors-quantity">
              {!selectedProduct ? (<h4 style={{ color: "#dc3545", textAlign: "center" }}>Choisissez un produit d'abord</h4>) : isSelectedProductFinished ? (Object.keys(selectedProduct.sizesOnly || {}).length === 0 ? (<h4 style={{ color: "#dc3545", textAlign: "center" }}>Aucune taille définie pour ce produit</h4>) : (<div className="color-sizes color-sizes--active">{Object.entries(selectedProduct.sizesOnly).map(([size, quantity]) => (<div key={size}><h4>{size}:</h4><input readOnly type="text" value={quantity} /></div>))}</div>)) : colors.length === 0 ? (<h4 style={{ color: "#dc3545", textAlign: "center" }}>Aucune couleur disponible pour ce produit</h4>) : (selectedColors.map((colorName, index) => { const sizes = selectedProduct.colorsAndSizes[colorName]; return (<div className={`color-sizes ${displayedColor === colorName ? 'color-sizes--active' : ''}`} key={index}><div className="choose-color" onClick={() => setDisplayedColor(colorName)} style={{ backgroundColor: colorName }}><button type="button" /></div>{sizes && Object.entries(sizes).map(([size, quantity]) => (<div key={size}><h4>{size}:</h4><input readOnly type="text" value={quantity} /></div>))}</div>); }))}
            </div>
            <div className="field-note">📦 Quantités disponibles {isSelectedProductFinished ? 'par taille' : 'pour les couleurs sélectionnées'}</div>
          </div>
          <div className="form-group"><h4>Tags du produit vierge</h4><div className="add-product-tags">{selectedProduct?.tags?.map((tag, index) => (<span key={index} style={{ background: "var(--surface-2)", color: "var(--text-secondary)" }}>{tag}<span style={{ fontSize: "0.7rem", marginLeft: "4px", opacity: 0.6 }}>(produit)</span></span>))}{(!selectedProduct?.tags || selectedProduct.tags.length === 0) && <div className="field-note" style={{ color: "var(--text-muted)", padding: "0.5rem 0" }}>Aucun tag associé à ce produit</div>}</div><div style={{ marginTop: "1rem" }}><h4>Tags du modèle (design)</h4><div className="add-product-tags">{formData.tags?.map((tag, index) => (<span key={index}>{tag}<button type="button" onClick={() => setFormData({ ...formData, tags: formData.tags.filter((_, i) => i !== index) })}>&times;</button></span>))}</div><div className="add-product-tags-input"><input type="text" placeholder="Ajouter un tag pour ce modèle" value={tagInput} onChange={(e) => setTagInput(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); handleAddTag(); } }} /><button type="button" onClick={handleAddTag}>Ajouter</button></div><div className="field-note" style={{ marginTop: "0.5rem" }}>🏷️ Les tags du produit sont hérités du produit vierge. Ajoutez des tags spécifiques à ce modèle.</div></div></div>
          {!isSelectedProductFinished && selectedColors.length > 0 && (<>
            <div style={{ borderTop: '2px solid var(--border)', marginTop: '1.5rem', paddingTop: '1.5rem', marginBottom: '1rem' }}><h3 style={{ fontSize: '1rem', textTransform: 'none', color: 'var(--accent)', marginBottom: '0.5rem', fontWeight: 600 }}>📥 Téléchargement des designs (Sauvegarde sur OVH)</h3><div className="field-note">⚡ Les designs seront sauvegardés directement sur OVH cloud storage (haute qualité)</div></div>
            <div className="form-group"><div className="flex-row"><div style={{ flex: 1 }}><h4 style={{ fontSize: "0.8rem", marginBottom: "0.5rem" }}>📤 Design devant ({frontDesigns.length})</h4><div className="add-product-input"><input type="file" accept="image/*" onChange={(e) => handleDesignUpload("front", e)} multiple /></div><div className="field-note">PNG, JPG, SVG - Plusieurs fichiers autorisés - Sauvegarde sur OVH</div></div><div style={{ flex: 1 }}><h4 style={{ fontSize: "0.8rem", marginBottom: "0.5rem" }}>📤 Design dos ({backDesigns.length})</h4><div className="add-product-input"><input type="file" accept="image/*" onChange={(e) => handleDesignUpload("back", e)} multiple /></div><div className="field-note">PNG, JPG, SVG - Plusieurs fichiers autorisés - Sauvegarde sur OVH</div></div></div></div>
            <div className="design-gallery-section">
              <div className="design-gallery-header">
                <div>
                  <h4>Images du modèle ({galleryImages.length})</h4>
                  <div className="field-note">Ajoutez 2, 3, 4 images ou plus. Elles seront aussi sauvegardées sur OVH dans le dossier designs.</div>
                </div>
                <span className="design-gallery-pill">OVH designs</span>
              </div>
              <div className="add-product-input"><input type="file" accept="image/*" onChange={handleGalleryImageUpload} multiple /></div>
              {galleryImages.length > 0 && (
                <div className="design-gallery-grid">
                  {galleryImages.map((image, index) => (
                    <div className="design-gallery-card" key={image.id}>
                      <img src={image.previewUrl} alt={image.name} />
                      <button type="button" className="design-gallery-remove" onClick={() => handleDeleteGalleryImage(image.id)} title="Supprimer l'image"><UilTrashAlt size={16} /></button>
                      <div className="design-gallery-meta">
                        <span>{index + 1}. {image.name}</span>
                        <small>{formatFileSize(image.size)}</small>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            {(frontDesigns.length > 0 || backDesigns.length > 0 || galleryImages.length > 0) && (<div className="quantity-container" style={{ marginBottom: '1rem', background: 'var(--accent-soft)' }}><h4>📊 Résumé des designs</h4><div className="field-note" style={{ fontWeight: 500 }}>Devant: {frontDesigns.length} design(s) | Dos: {backDesigns.length} design(s) | Images: {galleryImages.length}</div><div className="field-note">💡 Cliquez sur un design dans l'aperçu pour le déplacer/redimensionner</div><div className="field-note">🗑️ Cliquez sur la corbeille pour supprimer un design</div><div className="field-note">☁️ Les designs et images seront sauvegardés sur OVH cloud storage</div></div>)}
          </>)}
          {!isSelectedProductFinished && colors.length > 0 && selectedColors.length === 0 && (<div className="quantity-container" style={{ marginBottom: '1rem', background: '#fff8eb', border: '1px solid #ffd166' }}><div className="field-note" style={{ color: '#9a6200', textAlign: 'center' }}>🎨 Veuillez d'abord sélectionner au moins une couleur pour ce design</div></div>)}
          <div className="publish-buttons"><button onClick={handleSubmit} className="B2B-button" disabled={isSubmitting || (!isSelectedProductFinished && selectedColors.length === 0)}>{isSubmitting ? "Envoi en cours..." : (isSelectedProductFinished ? "✅ Ajouter le produit fini" : `🚀 Publier sur B2B (${selectedColors.length} couleur${selectedColors.length > 1 ? 's' : ''}) - OVH`)}</button><button className="B2C-button" disabled>🛒 Publier sur B2C (Bientôt)</button></div>
        </div>
        <div className="add-product-grid-item preview-panel">
          {!isSelectedProductFinished && selectedColors.length > 0 && (<div className="color-picker-bar"><span className="color-picker-label">🎨 Couleur</span><div className="color-picker-dots">{selectedColors.map((c) => (<button key={c} className={`color-picker-dot ${displayedColor === c ? 'active' : ''}`} style={{ backgroundColor: c }} onClick={() => setDisplayedColor(c)} title={c} />))}</div>{displayedColor && <span className="color-picker-value">{displayedColor}</span>}</div>)}
          {!isSelectedProductFinished && colors.length > 0 && selectedColors.length === 0 && (<div className="color-picker-bar" style={{ justifyContent: 'center', opacity: 0.6 }}><span className="color-picker-label">🎨 Sélectionnez une couleur ci-dessus</span></div>)}
          <div className="preview-section"><div className="preview-section-header"><h3>👕 DEVANT {frontDesigns.length > 0 && `(${frontDesigns.length} design${frontDesigns.length > 1 ? 's' : ''})`}</h3>{!isSelectedProductFinished && frontDesigns.length > 0 && (<button className="export-btn" onClick={() => handleExport("front")}><UilImageDownload size={14} /> Exporter 4K</button>)}</div><div className="preview-canvas">{!isSelectedProductFinished ? (<>{loadingFront && <div className="canvas-loading-overlay"><div className="spinner-small" /></div>}<Stage ref={frontStageRef} width={500} height={500} pixelRatio={window.devicePixelRatio || 2} style={{ imageSmoothingEnabled: true, imageSmoothingQuality: 'high' }}><Layer><Rect x={0} y={0} width={500} height={500} fill="white" onClick={() => handleBackgroundClick("front")} />{tintedFront && <KonvaImage image={tintedFront} width={500} height={500} imageSmoothingEnabled={true} imageSmoothingQuality="high" onClick={() => handleBackgroundClick("front")} />}{frontDesigns.map((design) => (<DesignItem key={design.id} design={design} isSelected={selectedDesignId === design.id && selectedSide === "front"} onSelect={() => handleSelectDesign("front", design.id)} onUpdate={(updated) => handleUpdateDesign("front", updated)} onDelete={() => handleDeleteDesign("front", design.id)} />))}</Layer></Stage></>) : (<div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f5f5f5', borderRadius: '20px' }}>{productFrontImageUrl ? (<img src={productFrontImageUrl} alt="Product front" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />) : (<p>Image devant non disponible</p>)}</div>)}</div></div>
          <div className="preview-section"><div className="preview-section-header"><h3>👕 DOS {backDesigns.length > 0 && `(${backDesigns.length} design${backDesigns.length > 1 ? 's' : ''})`}</h3>{!isSelectedProductFinished && backDesigns.length > 0 && (<button className="export-btn" onClick={() => handleExport("back")}><UilImageDownload size={14} /> Exporter 4K</button>)}</div><div className="preview-canvas">{!isSelectedProductFinished ? (<>{loadingBack && <div className="canvas-loading-overlay"><div className="spinner-small" /></div>}<Stage ref={backStageRef} width={500} height={500} pixelRatio={window.devicePixelRatio || 2} style={{ imageSmoothingEnabled: true, imageSmoothingQuality: 'high' }}><Layer><Rect x={0} y={0} width={500} height={500} fill="white" onClick={() => handleBackgroundClick("back")} />{tintedBack && <KonvaImage image={tintedBack} width={500} height={500} imageSmoothingEnabled={true} imageSmoothingQuality="high" onClick={() => handleBackgroundClick("back")} />}{backDesigns.map((design) => (<DesignItem key={design.id} design={design} isSelected={selectedDesignId === design.id && selectedSide === "back"} onSelect={() => handleSelectDesign("back", design.id)} onUpdate={(updated) => handleUpdateDesign("back", updated)} onDelete={() => handleDeleteDesign("back", design.id)} />))}</Layer></Stage></>) : (<div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f5f5f5', borderRadius: '20px' }}>{productBackImageUrl ? (<img src={productBackImageUrl} alt="Product back" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />) : (<p>Image dos non disponible</p>)}</div>)}</div></div>
          {selectedProduct && <div className="product-loaded-badge">✅ Produit sélectionné: {selectedProduct._id}</div>}
        </div>
      </div>
      <ToastContainer position="top-right" />
    </div>
  );
};
